import React, { useState } from "react";
import { shape, string } from "prop-types";
import { Button, Typography } from "antd";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import {
  EMAIL_TEMPLATE_PLACEHOLDER_REGEX,
  getPlaceholderValue,
  isMissingPlaceholderValue,
} from "@stack/shared";
import styles from "./AddNewCampaign.module.css";
import { useEmailTemplatePreview } from "./EmailTemplatePreviewContext";

const { Text } = Typography;

// The {{object.field}} placeholders in the html, in order of first appearance
const getPlaceholderPaths = html => [
  ...new Set(
    [...(html || "").matchAll(EMAIL_TEMPLATE_PLACEHOLDER_REGEX)].map(
      ([, path]) => path
    )
  ),
];

// The expected value for a placeholder, from the campaign being created, or why there isn't one yet.
// Emails are filled in when they're sent, so values like campaign.link can differ.
const getPreview = (values, path) => {
  if (path.startsWith("onboard.")) return { note: "Set per influencer" };
  if (path === "campaign.campaignId") {
    return { note: "Assigned when the campaign is created" };
  }

  const value = getPlaceholderValue(values, path);
  if (isMissingPlaceholderValue(value)) return { note: "Not set yet" };
  return { value: String(value) };
};

const PlaceholderPreview = ({ preview }) => {
  if (preview.note) {
    return (
      <Text type="secondary" italic className={styles.placeholderPreview}>
        {preview.note}
      </Text>
    );
  }
  return (
    <Text
      className={styles.placeholderPreview}
      ellipsis={{ tooltip: preview.value }}
    >
      {preview.value}
    </Text>
  );
};

PlaceholderPreview.propTypes = {
  preview: shape({ value: string, note: string }).isRequired,
};

// Collapsible column listing the {{object.field}} placeholders in the template's html and their
// expected values. Absolutely positioned to the right of the editor (see .emailTemplateField), so it
// doesn't take space from it.
const EmailTemplateVariables = ({ html }) => {
  const [expanded, setExpanded] = useState(true);
  const { values } = useEmailTemplatePreview();
  const paths = getPlaceholderPaths(html);

  return (
    <aside
      className={`${styles.emailTemplateVariables} ${
        expanded ? "" : styles.emailTemplateVariablesCollapsed
      }`}
    >
      <Button
        type="text"
        size="small"
        icon={expanded ? <RightOutlined /> : <LeftOutlined />}
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
      >
        Placeholders
      </Button>
      {expanded && paths.length === 0 && (
        <Text type="secondary" italic className={styles.placeholderPreview}>
          No placeholders in this template
        </Text>
      )}
      {expanded && (
        <>
          <p>
            Add variables to the email template with{" "}
            <code>{`{{object.field}}`}</code>, for example{" "}
            <code>{`{{campaign.link}}`}</code>. You can use fields from{" "}
            <code>onboard</code>, <code>campaign</code>, <code>contract</code>,{" "}
            <code>company</code>, and <code>product</code>.
          </p>
          {paths.map(path => (
            <div key={path} className={styles.emailTemplateVariablesGroup}>
              <Text code>{`{{${path}}}`}</Text>
              <PlaceholderPreview preview={getPreview(values, path)} />
            </div>
          ))}
        </>
      )}
    </aside>
  );
};

EmailTemplateVariables.propTypes = {
  html: string,
};

export default EmailTemplateVariables;
